import { useEffect } from 'react';

/**
 * [모듈 6] 배경 3D 장식(.sec-deco span) 스크롤 패럴럭스.
 * 섹션 배경의 소프트 오브·유리 버블을 스크롤 진행에 맞춰 살짝 이동시킨다.
 *
 * 원본은 document 전체에서 `.sec-deco span`을 찾아 각 span의 closest('section')을 기준으로
 * 이동량을 계산했다. 여기서는 섹션마다 컴포넌트가 나뉘어 있으므로, 장식을 가진 섹션이
 * 자신의 .sec-deco 컨테이너 ref를 넘겨 그 안의 span만 스코프해서 계산한다(수식·상수는 원본 그대로).
 *
 * @param {import('react').RefObject<HTMLElement>} containerRef - .sec-deco 래퍼 요소의 ref
 */
export default function useDecoParallax(containerRef) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const shapes = Array.from(container.querySelectorAll('span'));
    if (!shapes.length) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    shapes.forEach((s, i) => {
      s._pspeed = 0.02 + (i % 3) * 0.02; // 0.02 ~ 0.06 (깊이감 차등)
    });
    const CAP = 55; // 이동량 상한(px) — 과하게 튀지 않도록
    const section = container.closest('section') || container;
    let ticking = false;

    function update() {
      const vh = window.innerHeight;
      const r = section.getBoundingClientRect();
      const offset = r.top + r.height / 2 - vh / 2;
      shapes.forEach((s) => {
        let t = -offset * s._pspeed;
        if (t > CAP) t = CAP;
        else if (t < -CAP) t = -CAP;
        s.style.transform = `translate3d(0,${t.toFixed(1)}px,0)`;
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [containerRef]);
}
